<script setup lang="ts">
/**
 * H4 — Capsules section carousel + persona subtitle
 * Figma RedisignHomePage `15:9530` (personas `15:9631` / `15:9652` / `15:9673`) / compact `15:9551`
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import Skeleton from 'primevue/skeleton'
import {
  fairScrollLeftTo,
  useFairDragScroll,
} from '@/composables/useFairDragScroll'
import { useAuthModal, useTokenStore } from '@/features/auth/store'
import { resolveCapsulesPersonalization } from '@/features/capsules/api/capsules.data'
import { useTasteClarificationModal } from '@/features/taste/store'
import { useHomeCapsulesPreviewQuery } from '@/queries/capsules'
import { FairCapsuleCardV2 } from '@/ui/patterns/FairCapsuleCardV2'
import { FairLink } from '@/ui/primitives/FairLink'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import { FairIconContainer } from '@/ui/primitives/FairIconContainer'
import { FairPaginationBar } from '@/ui/primitives/FairPaginationBar'
import {
  FAIR_HOME_V2_CAPSULES_LIMIT,
  getFairHomeV2CapsulesCardWidth,
  getFairHomeV2CapsulesCardsPerView,
  getFairHomeV2CapsulesGap,
  getFairHomeV2CapsulesLookHeight,
  getFairHomeV2CapsulesPageCount,
  mapFairHomeV2CapsulesAudience,
  normalizeFairHomeV2CapsulesPage,
  resolveFairHomeV2CapsuleSlides,
  resolveFairHomeV2CapsulesPersona,
  resolveFairHomeV2CapsulesScrollPage,
  type FairHomeV2CapsuleSlide,
} from './fair-home-v2-capsules.data'
import './fair-home-v2-capsules.css'

const props = withDefaults(
  defineProps<{
    compact?: boolean
    audience?: string
    viewportWidth?: number
  }>(),
  {
    compact: false,
    audience: 'women',
    viewportWidth: 1440,
  },
)

const { t } = useI18n()
const router = useRouter()
const authModal = useAuthModal()
const tokenStore = useTokenStore()
const tasteStore = useTasteClarificationModal()

const isAuthenticated = computed(() => tokenStore.isAuthenticated())

const page = ref(0)
const viewportRef = ref<HTMLElement | null>(null)
const lookIndexByCapsuleId = reactive<Record<number, number>>({})

useFairDragScroll(viewportRef)

let rowScrollTarget: HTMLElement | null = null

const apiAudience = computed(() => mapFairHomeV2CapsulesAudience(props.audience))

const capsulesQuery = useHomeCapsulesPreviewQuery(
  computed(() => ({
    limit: FAIR_HOME_V2_CAPSULES_LIMIT,
    audience: apiAudience.value,
  })),
)

const slides = computed(() =>
  resolveFairHomeV2CapsuleSlides(capsulesQuery.data.value),
)

const isLoading = computed(
  () => capsulesQuery.isLoading.value && capsulesQuery.data.value == null,
)

const personalization = computed(() =>
  resolveCapsulesPersonalization(capsulesQuery.data.value),
)

const personaView = computed(() =>
  resolveFairHomeV2CapsulesPersona({
    isAuthenticated: isAuthenticated.value,
    personalization: personalization.value,
  }),
)

const sectionTitle = computed(() => t(personaView.value.titleKey))

const sectionSubtitle = computed(() =>
  t(personaView.value.subtitleKey, personaView.value.subtitleParams ?? {}),
)

const showSubtitleLink = computed(() => Boolean(personaView.value.linkKey))

const subtitleLinkLabel = computed(() =>
  personaView.value.linkKey ? t(personaView.value.linkKey) : '',
)

const cardSize = computed(() => (props.compact ? 'mobile' : 'default'))

const cardWidth = computed(() => getFairHomeV2CapsulesCardWidth(props.compact))

const cardGap = computed(() => getFairHomeV2CapsulesGap(props.compact))

const lookHeight = computed(() => getFairHomeV2CapsulesLookHeight(props.compact))

const cardsPerView = computed(() =>
  getFairHomeV2CapsulesCardsPerView(props.compact, props.viewportWidth),
)

const pageCount = computed(() =>
  getFairHomeV2CapsulesPageCount(slides.value.length, cardsPerView.value),
)

const safePage = computed(() =>
  normalizeFairHomeV2CapsulesPage(page.value, pageCount.value),
)

const showNav = computed(() => !props.compact && slides.value.length > cardsPerView.value)

const canPrev = computed(() => safePage.value > 0)
const canNext = computed(() => safePage.value < pageCount.value - 1)

const showPager = computed(
  () => slides.value.length > 1 && pageCount.value > 1,
)

const sectionStyle = computed(() => ({
  '--fair-h2-capsules-gap': `${cardGap.value}px`,
  '--fair-h2-capsules-card-width': `${cardWidth.value}px`,
  '--fair-h2-capsules-look-height': `${lookHeight.value}px`,
}))

const skeletonCount = computed(() => Math.min(FAIR_HOME_V2_CAPSULES_LIMIT, cardsPerView.value + 1))

const rootClass = computed(() => ({
  'fair-home-v2-capsules': true,
  'fair-home-v2-capsules--compact': props.compact,
  'fair-home-v2-capsules--desktop': !props.compact,
}))

watch(
  () => slides.value.length,
  () => {
    page.value = normalizeFairHomeV2CapsulesPage(page.value, pageCount.value)
    void nextTick(() => {
      scrollToPage(page.value, 'auto')
      syncScrollPage()
    })
  },
)

watch(
  () => props.compact,
  () => {
    page.value = 0
    void nextTick(() => {
      scrollToPage(0, 'auto')
    })
  },
)

watch(
  () => props.viewportWidth,
  () => {
    page.value = normalizeFairHomeV2CapsulesPage(page.value, pageCount.value)
    void nextTick(() => {
      scrollToPage(page.value, 'auto')
    })
  },
)

function scrollStep(): number {
  return cardWidth.value + cardGap.value
}

function scrollToPage(index: number, behavior: ScrollBehavior = 'smooth') {
  const el = viewportRef.value
  if (!el) return
  const step = scrollStep()
  if (step <= 0) return
  const next = normalizeFairHomeV2CapsulesPage(index, pageCount.value)
  page.value = next
  const left = next * step
  if (behavior === 'auto') {
    el.scrollLeft = left
    return
  }
  fairScrollLeftTo(el, left, { durationMs: 720 })
}

function unbindRowScroll() {
  if (!rowScrollTarget) return
  rowScrollTarget.removeEventListener('scroll', handleScroll)
  rowScrollTarget = null
}

function bindRowScroll(el: HTMLElement | null) {
  unbindRowScroll()
  if (!el) return
  el.addEventListener('scroll', handleScroll, { passive: true })
  rowScrollTarget = el
  syncScrollPage()
}

function syncScrollPage() {
  const el = viewportRef.value
  if (!el) return
  const step = scrollStep()
  if (step <= 0) return
  page.value = resolveFairHomeV2CapsulesScrollPage(
    el.scrollLeft,
    step,
    pageCount.value,
  )
}

function getLookIndex(capsuleId: number): number {
  return lookIndexByCapsuleId[capsuleId] ?? 0
}

function setLookIndex(capsuleId: number, index: number) {
  lookIndexByCapsuleId[capsuleId] = index
}

function capsuleTo(slide: FairHomeV2CapsuleSlide) {
  return {
    name: 'capsule' as const,
    params: { capsuleId: String(slide.id) },
  }
}

function handlePrev() {
  if (!canPrev.value) return
  scrollToPage(safePage.value - 1)
}

function handleNext() {
  if (!canNext.value) return
  scrollToPage(safePage.value + 1)
}

function handlePager(index: number) {
  scrollToPage(index)
}

function handleScroll() {
  syncScrollPage()
}

function handleCapsuleClick(slide: FairHomeV2CapsuleSlide) {
  void router.push({
    name: 'capsule',
    params: { capsuleId: String(slide.id) },
  })
}

function handleSubtitleLinkClick() {
  if (personaView.value.linkAction === 'auth') {
    authModal.showAuthModal('signInSignUpWrapper')
    return
  }
  void tasteStore.showTasteModal()
}

watch(viewportRef, (el) => {
  bindRowScroll(el)
})

onMounted(() => {
  bindRowScroll(viewportRef.value)
})

onBeforeUnmount(() => {
  unbindRowScroll()
})
</script>

<template>
  <section
    :class="rootClass"
    :style="sectionStyle"
    aria-label="capsules"
    :data-edition-kind="personalization.editionKind"
    :data-tuned="personalization.tuned ? 'true' : 'false'"
  >
    <header class="fair-home-v2-capsules__heading">
      <div class="fair-home-v2-capsules__heading-text">
        <h2 class="fair-home-v2-capsules__title">
          {{ sectionTitle }}
        </h2>
        <div class="fair-home-v2-capsules__subtitle-row">
          <div class="fair-home-v2-capsules__subtitle-copy">
            <FairIconContainer
              v-if="personaView.showAiIcon"
              class="fair-home-v2-capsules__subtitle-icon"
              name="ai"
              :size="12"
            />
            <p class="fair-home-v2-capsules__subtitle">
              {{ sectionSubtitle }}
            </p>
          </div>
          <FairLink
            v-if="showSubtitleLink && !compact"
            class="fair-home-v2-capsules__subtitle-link"
            type="static"
            :label="subtitleLinkLabel"
            @click="handleSubtitleLinkClick"
          />
          <button
            v-else-if="showSubtitleLink && compact"
            type="button"
            class="fair-home-v2-capsules__improve-chip"
            @click="handleSubtitleLinkClick"
          >
            {{ subtitleLinkLabel }}
          </button>
        </div>
      </div>
      <div
        v-if="showNav"
        class="fair-home-v2-capsules__nav"
      >
        <FairIconButton
          icon="arrow-back"
          size="small"
          :disabled="!canPrev"
          :aria-label="t('fair_home_v2.capsules.carousel_prev')"
          @click="handlePrev"
        />
        <FairIconButton
          icon="arrow-forward"
          size="small"
          :disabled="!canNext"
          :aria-label="t('fair_home_v2.capsules.carousel_next')"
          @click="handleNext"
        />
      </div>
    </header>

    <div
      v-if="isLoading"
      class="fair-home-v2-capsules__row fair-home-v2-capsules__row--loading"
      role="status"
      :aria-label="t('fair_home_v2.capsules.loading')"
    >
      <div
        v-for="index in skeletonCount"
        :key="index"
        class="fair-home-v2-capsules__skeleton"
      >
        <Skeleton class="fair-home-v2-capsules__skeleton-look" />
        <Skeleton class="fair-home-v2-capsules__skeleton-title" />
        <Skeleton class="fair-home-v2-capsules__skeleton-text" />
      </div>
    </div>

    <div
      v-else-if="slides.length"
      ref="viewportRef"
      class="fair-home-v2-capsules__row"
      @scroll.passive="handleScroll"
    >
      <FairCapsuleCardV2
        v-for="slide in slides"
        :key="slide.id"
        class="fair-home-v2-capsules__card"
        :title="slide.title"
        :description="slide.description"
        :item-count="slide.itemCount"
        :looks="slide.looks"
        :look-index="getLookIndex(slide.id)"
        :size="cardSize"
        :to="capsuleTo(slide)"
        @update:look-index="setLookIndex(slide.id, $event)"
        @click="handleCapsuleClick(slide)"
      />
    </div>

    <p
      v-else
      class="fair-home-v2-capsules__empty"
    >
      {{ t('fair_home_v2.capsules.empty') }}
    </p>

    <div
      v-if="showPager"
      class="fair-home-v2-capsules__pager"
    >
      <FairPaginationBar
        :count="pageCount"
        :model-value="safePage"
        @update:model-value="handlePager"
      />
    </div>
  </section>
</template>
